import type {
  ProductPaymentGatewaySettings,
  UpdateProductPaymentGatewayPayload,
} from '@/services';
import { z } from 'zod';
import { GATEWAY_METHOD_ORDER } from '../utils/payment-settings-labels.util';

const wholeNumberBetween = (min: number, max: number, message: string) =>
  z
    .string()
    .trim()
    .regex(/^\d+$/, 'Informe um número inteiro.')
    .refine(value => Number(value) >= min && Number(value) <= max, message);

/**
 * Campos da aba "Cobrança e formas". Os números são texto (é o que o
 * `NumberField` entrega); a conversão acontece em `toUpdatePaymentGatewayPayload`.
 */
export const paymentGatewayFieldsSchema = z.object({
  enabled: z.boolean(),
  offerPix: z.boolean(),
  offerCreditCard: z.boolean(),
  offerBoletoPix: z.boolean(),
  cardBillingMode: z.enum(['PER_INSTALLMENT', 'SINGLE_TRANSACTION']),
  pixBillingMode: z.enum(['SCHEDULED', 'AUTOMATIC']),
  chargeLeadDays: wholeNumberBetween(
    1,
    30,
    'A antecedência vai de 1 a 30 dias.'
  ),
  expireGraceDays: wholeNumberBetween(0, 30, 'Informe de 0 a 30 dias.'),
  notifyCustomerByEmail: z.boolean(),
  redirectUrl: z
    .string()
    .trim()
    .refine(
      value => value === '' || /^https:\/\/\S+\.\S+/.test(value),
      'Informe um endereço https completo.'
    ),
});

export type PaymentGatewayFormValues = z.infer<
  typeof paymentGatewayFieldsSchema
>;

export const NO_METHOD_WHILE_ENABLED_MESSAGE =
  'Escolha ao menos uma forma para manter a cobrança pelo gateway ligada.';

/** A regra que cruza campos: ligado exige ao menos uma forma. */
export function gatewayMethodsError(
  values: PaymentGatewayFormValues
): string | undefined {
  const anyMethod =
    values.offerPix || values.offerCreditCard || values.offerBoletoPix;
  return values.enabled && !anyMethod
    ? NO_METHOD_WHILE_ENABLED_MESSAGE
    : undefined;
}

export function toPaymentGatewayFormValues(
  gateway: ProductPaymentGatewaySettings
): PaymentGatewayFormValues {
  return {
    enabled: gateway.enabled,
    offerPix: gateway.enabledMethods.includes('PIX'),
    offerCreditCard: gateway.enabledMethods.includes('CREDIT_CARD'),
    offerBoletoPix: gateway.enabledMethods.includes('BOLETO_PIX'),
    cardBillingMode: gateway.cardBillingMode,
    pixBillingMode: gateway.pixBillingMode,
    chargeLeadDays: String(gateway.chargeLeadDays),
    expireGraceDays: String(gateway.expireGraceDays),
    notifyCustomerByEmail: gateway.notifyCustomerByEmail,
    redirectUrl: gateway.redirectUrl ?? '',
  };
}

export function toUpdatePaymentGatewayPayload(
  values: PaymentGatewayFormValues,
  version: number
): UpdateProductPaymentGatewayPayload {
  const offered = {
    PIX: values.offerPix,
    CREDIT_CARD: values.offerCreditCard,
    BOLETO_PIX: values.offerBoletoPix,
  };

  return {
    enabled: values.enabled,
    enabledMethods: GATEWAY_METHOD_ORDER.filter(method => offered[method]),
    cardBillingMode: values.cardBillingMode,
    pixBillingMode: values.pixBillingMode,
    chargeLeadDays: Number(values.chargeLeadDays),
    expireGraceDays: Number(values.expireGraceDays),
    notifyCustomerByEmail: values.notifyCustomerByEmail,
    redirectUrl: values.redirectUrl.trim() || undefined,
    version,
  };
}
